import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/context/auth.context";
import { motion } from "framer-motion";
import {
  Calendar, Clock, MapPin, Users, CheckCircle, XCircle, Plus, Edit, Trash2,
  Eye, FileText, Printer, Download, Search, Filter, MoreHorizontal, Copy,
  Ban, Play, CheckSquare, AlertTriangle, ChevronDown, Link as LinkIcon,
  Paperclip, MessageSquare, ListTodo, CalendarClock, UserCheck, UserX,
  Clock3, ShieldCheck, TrendingUp, BarChart3, FileDown, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MEETING_TYPES = [
  { value: "general_assembly", label: "General Assembly", color: "bg-blue-100 text-blue-800" },
  { value: "committee", label: "Committee", color: "bg-purple-100 text-purple-800" },
  { value: "loan_review", label: "Loan Review", color: "bg-amber-100 text-amber-800" },
  { value: "savings_review", label: "Savings Review", color: "bg-green-100 text-green-800" },
  { value: "emergency", label: "Emergency", color: "bg-red-100 text-red-800" },
];

const STATUS_CONFIG: Record<string, { color: string; icon: any }> = {
  draft: { color: "bg-gray-100 text-gray-800", icon: FileText },
  scheduled: { color: "bg-blue-100 text-blue-800", icon: CalendarClock },
  ongoing: { color: "bg-yellow-100 text-yellow-800", icon: Play },
  completed: { color: "bg-green-100 text-green-800", icon: CheckCircle },
  cancelled: { color: "bg-red-100 text-red-800", icon: Ban },
};

const PRIORITY_CONFIG: Record<string, string> = {
  low: "bg-gray-100 text-gray-800",
  medium: "bg-blue-100 text-blue-800",
  high: "bg-amber-100 text-amber-800",
  urgent: "bg-red-100 text-red-800",
};

export default function Meetings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isAttendanceOpen, setIsAttendanceOpen] = useState(false);
  const [isMinutesOpen, setIsMinutesOpen] = useState(false);
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [isActionItemOpen, setIsActionItemOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
  const [editingMeeting, setEditingMeeting] = useState<any>(null);

  // ─── Form State ───
  const [form, setForm] = useState({
    title: "", meetingType: "general_assembly", meetingDate: "", startTime: "09:00",
    endTime: "11:00", location: "", onlineLink: "", agenda: "", notes: "",
    requiredAttendance: true,
  });
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [minutesContent, setMinutesContent] = useState("");
  const [decisionForm, setDecisionForm] = useState({ description: "", category: "", notes: "" });
  const [actionItemForm, setActionItemForm] = useState({
    description: "", assignedToId: "", dueDate: "", priority: "medium", notes: "",
  });

  // ─── Data Fetching ───
  const { data: dashboardStats, isLoading: statsLoading } = useQuery(
    ["meetings-dashboard"],
    async () => { const { data } = await api.get("/meetings/dashboard/stats"); return data?.data; }
  );

  const { data: meetingsData, isLoading: meetingsLoading, refetch: refetchMeetings } = useQuery(
    ["meetings", statusFilter, typeFilter, search],
    async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (typeFilter !== "all") params.append("type", typeFilter);
      if (search) params.append("search", search);
      const { data } = await api.get(`/meetings?${params.toString()}`);
      return data;
    }
  );

  const { data: teachers = [] } = useQuery(
    ["teachers-list"],
    async () => { const { data } = await api.get("/teachers?limit=1000"); return data?.results || data?.data || []; }
  );

  const meetings = meetingsData?.data || meetingsData?.results || [];

  // ─── Mutations ───
  const createMutation = useMutation(
    async (formData: any) => { const { data } = await api.post("/meetings", formData); return data; },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["meetings", "meetings-dashboard"]);
        setIsCreateOpen(false);
        resetForm();
        toast.success("Meeting created successfully");
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || "Failed to create meeting"); },
    }
  );

  const updateMutation = useMutation(
    async ({ id, ...formData }: any) => { const { data } = await api.patch(`/meetings/${id}`, formData); return data; },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["meetings", "meetings-dashboard"]);
        setIsEditOpen(false);
        setEditingMeeting(null);
        toast.success("Meeting updated");
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || "Failed to update"); },
    }
  );

  const deleteMutation = useMutation(
    async (id: number) => { await api.delete(`/meetings/${id}`); },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["meetings", "meetings-dashboard"]);
        toast.success("Meeting deleted");
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || "Failed to delete"); },
    }
  );

  const statusMutation = useMutation(
    async ({ id, action, payload }: any) => { const { data } = await api.patch(`/meetings/${id}/${action}`, payload || {}); return data; },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["meetings", "meetings-dashboard"]);
        toast.success(`Meeting ${variables.action === "cancel" ? "cancelled" : variables.action === "start" ? "started" : variables.action === "complete" ? "completed" : "updated"}`);
        if (variables.action === "complete") setIsAttendanceOpen(false);
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || "Failed"); },
    }
  );

  const duplicateMutation = useMutation(
    async (id: number) => { const { data } = await api.post(`/meetings/${id}/duplicate`); return data; },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["meetings"]);
        toast.success("Meeting duplicated");
      },
    }
  );

  const bulkAttendanceMutation = useMutation(
    async ({ meetingId, records }: any) => { const { data } = await api.post(`/meetings/${meetingId}/attendance/bulk`, { records }); return data; },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["meetings"]);
        toast.success("Attendance saved");
        setIsAttendanceOpen(false);
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || "Failed"); },
    }
  );

  const minutesMutation = useMutation(
    async ({ meetingId, content, title }: any) => { const { data } = await api.post(`/meetings/${meetingId}/minutes`, { content, title }); return data; },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["meetings"]);
        toast.success("Minutes saved");
        setIsMinutesOpen(false);
      },
    }
  );

  const publishMinutesMutation = useMutation(
    async ({ meetingId, minutesId }: any) => { const { data } = await api.patch(`/meetings/${meetingId}/minutes/${minutesId}/publish`); return data; },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["meetings"]);
        toast.success("Minutes published");
      },
    }
  );

  const decisionMutation = useMutation(
    async ({ meetingId, ...formData }: any) => { const { data } = await api.post(`/meetings/${meetingId}/decisions`, formData); return data; },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["meetings"]);
        toast.success("Decision recorded");
        setIsDecisionOpen(false);
        setDecisionForm({ description: "", category: "", notes: "" });
      },
    }
  );

  const actionItemMutation = useMutation(
    async ({ meetingId, ...formData }: any) => { const { data } = await api.post(`/meetings/${meetingId}/action-items`, formData); return data; },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["meetings"]);
        toast.success("Action item created");
        setIsActionItemOpen(false);
        setActionItemForm({ description: "", assignedToId: "", dueDate: "", priority: "medium", notes: "" });
      },
    }
  );

  const updateActionItemMutation = useMutation(
    async ({ itemId, ...formData }: any) => { const { data } = await api.patch(`/meetings/action-items/${itemId}`, formData); return data; },
    { onSuccess: () => queryClient.invalidateQueries(["meetings"]) }
  );

  // ─── Helpers ───
  const resetForm = () => setForm({
    title: "", meetingType: "general_assembly", meetingDate: "", startTime: "09:00",
    endTime: "11:00", location: "", onlineLink: "", agenda: "", notes: "",
    requiredAttendance: true,
  });

  const getTypeConfig = (type: string) => MEETING_TYPES.find((t) => t.value === type) || MEETING_TYPES[0];
  const getStatusConfig = (status: string) => STATUS_CONFIG[status] || STATUS_CONFIG.draft;

  const getAttendanceRate = (meeting: any) => {
    if (!meeting.totalMembers || meeting.totalMembers === 0) return 0;
    return Math.round(((meeting.attendanceCount || 0) / meeting.totalMembers) * 100);
  };

  const openDetail = (meeting: any) => { setSelectedMeeting(meeting); setIsDetailOpen(true); };
  const openEdit = (meeting: any) => {
    setEditingMeeting(meeting);
    setForm({
      title: meeting.title || "", meetingType: meeting.meetingType || "general_assembly",
      meetingDate: meeting.meetingDate?.split("T")[0] || "", startTime: meeting.startTime || "09:00",
      endTime: meeting.endTime || "11:00", location: meeting.location || "",
      onlineLink: meeting.onlineLink || "", agenda: meeting.agenda || "",
      notes: meeting.notes || "", requiredAttendance: meeting.requiredAttendance !== false,
    });
    setIsEditOpen(true);
  };

  const openAttendance = async (meeting: any) => {
    setSelectedMeeting(meeting);
    try {
      const { data } = await api.get(`/meetings/${meeting.id}`);
      const fullMeeting = data?.data;
      setAttendanceRecords(
        (fullMeeting?.attendances || []).map((a: any) => ({
          teacherId: a.teacher?.id,
          teacherName: a.teacher?.fullName || `${a.teacher?.firstName || ""} ${a.teacher?.lastName || ""}`.trim(),
          status: a.status || (a.isPresent ? "present" : "absent"),
          excuse: a.excuse || "",
          notes: a.notes || "",
        }))
      );
    } catch {
      setAttendanceRecords([]);
    }
    setIsAttendanceOpen(true);
  };

  const openMinutes = (meeting: any) => {
    setSelectedMeeting(meeting);
    setMinutesContent(meeting.minutes || "");
    setIsMinutesOpen(true);
  };

  const filteredMeetings = useMemo(() => meetings, [meetings]);

  // ─── Print Handlers ───
  const printMeetingNotice = async (meeting: any) => {
    const logoEl = document.querySelector('[data-cooperative-logo]') as HTMLImageElement;
    const logoSrc = logoEl?.src || '';
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Meeting Notice - ${meeting.meetingCode}</title>
      <style>body{font-family:Arial,sans-serif;padding:40px;max-width:800px;margin:0 auto}
      .header{text-align:center;border-bottom:3px solid #009A44;padding-bottom:20px;margin-bottom:30px}
      .title{font-size:24px;font-weight:bold;color:#009A44;margin:10px 0}
      .field{margin:12px 0;display:flex;gap:8px}.label{font-weight:bold;min-width:140px;color:#333}
      .value{color:#555}.badge{display:inline-block;padding:4px 12px;border-radius:12px;font-size:12px;font-weight:600}
      .footer{margin-top:40px;border-top:1px solid #ddd;padding-top:20px;text-align:center;font-size:12px;color:#888}
      </style></head><body>
      <div class="header">${logoSrc ? `<img src="${logoSrc}" style="height:60px;margin-bottom:10px">` : ''}
      <div class="title">MEETING NOTICE</div>
      <p style="color:#666">Teachers Savings Cooperative</p></div>
      <div class="field"><span class="label">Meeting ID:</span><span class="value">${meeting.meetingCode}</span></div>
      <div class="field"><span class="label">Title:</span><span class="value">${meeting.title}</span></div>
      <div class="field"><span class="label">Type:</span><span class="value"><span class="badge" style="background:#e8f5e9;color:#2e7d32">${getTypeConfig(meeting.meetingType).label}</span></span></div>
      <div class="field"><span class="label">Date:</span><span class="value">${format(new Date(meeting.meetingDate), "EEEE, MMMM d, yyyy")}</span></div>
      <div class="field"><span class="label">Time:</span><span class="value">${meeting.startTime} - ${meeting.endTime}</span></div>
      <div class="field"><span class="label">Location:</span><span class="value">${meeting.location}</span></div>
      ${meeting.onlineLink ? `<div class="field"><span class="label">Online Link:</span><span class="value">${meeting.onlineLink}</span></div>` : ''}
      ${meeting.agenda ? `<div class="field"><span class="label">Agenda:</span><span class="value" style="white-space:pre-wrap">${meeting.agenda}</span></div>` : ''}
      <div class="field"><span class="label">Required Attendance:</span><span class="value">${meeting.requiredAttendance ? "Yes" : "No"}</span></div>
      <div class="footer"><p>This is an official meeting notice. Please confirm your attendance.</p>
      <p>Generated on ${format(new Date(), "MMMM d, yyyy 'at' HH:mm")}</p></div>
      </body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const printAttendanceSheet = async (meeting: any) => {
    const { data } = await api.get(`/meetings/${meeting.id}`);
    const full = data?.data;
    const atts = full?.attendances || [];
    const logoEl = document.querySelector('[data-cooperative-logo]') as HTMLImageElement;
    const logoSrc = logoEl?.src || '';
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Attendance Sheet - ${meeting.meetingCode}</title>
      <style>body{font-family:Arial,sans-serif;padding:40px}
      .header{text-align:center;border-bottom:3px solid #009A44;padding-bottom:15px;margin-bottom:25px}
      table{width:100%;border-collapse:collapse;margin-top:20px}
      th{background:#f5f5f5;padding:10px;text-align:left;border:1px solid #ddd;font-size:13px}
      td{padding:10px;border:1px solid #ddd;font-size:13px}
      .sign{width:120px}</style></head><body>
      <div class="header">${logoSrc ? `<img src="${logoSrc}" style="height:50px">` : ''}
      <h2 style="margin:5px 0">ATTENDANCE SHEET</h2>
      <p>${meeting.title} - ${meeting.meetingCode}</p></div>
      <p><strong>Date:</strong> ${format(new Date(meeting.meetingDate), "MMMM d, yyyy")} | <strong>Time:</strong> ${meeting.startTime} - ${meeting.endTime} | <strong>Location:</strong> ${meeting.location}</p>
      <table><thead><tr><th>#</th><th>Member Name</th><th>Member ID</th><th>Status</th><th>Signature</th></tr></thead>
      <tbody>${atts.map((a: any, i: number) => `<tr><td>${i + 1}</td><td>${a.teacher?.fullName || ""}</td><td>${a.teacher?.teacherId || ""}</td><td>${a.status || "absent"}</td><td class="sign"></td></tr>`).join("")}</tbody></table>
      <p style="margin-top:30px;font-size:12px;color:#888">Total: ${atts.length} members | Present: ${atts.filter((a: any) => a.status === "present" || a.status === "late").length}</p>
      </body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const printMinutes = (meeting: any) => {
    const logoEl = document.querySelector('[data-cooperative-logo]') as HTMLImageElement;
    const logoSrc = logoEl?.src || '';
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Minutes - ${meeting.meetingCode}</title>
      <style>body{font-family:Arial,sans-serif;padding:40px;max-width:800px;margin:0 auto}
      .header{text-align:center;border-bottom:3px solid #009A44;padding-bottom:15px;margin-bottom:25px}
      .content{line-height:1.8;white-space:pre-wrap}
      .field{margin:8px 0}.label{font-weight:bold;color:#333}.value{color:#555}</style></head><body>
      <div class="header">${logoSrc ? `<img src="${logoSrc}" style="height:50px">` : ''}
      <h2 style="margin:5px 0">MEETING MINUTES</h2>
      <p>${meeting.title} - ${meeting.meetingCode}</p></div>
      <div class="field"><span class="label">Date:</span> <span class="value">${format(new Date(meeting.meetingDate), "MMMM d, yyyy")}</span></div>
      <div class="field"><span class="label">Time:</span> <span class="value">${meeting.startTime} - ${meeting.endTime}</span></div>
      <div class="field"><span class="label">Location:</span> <span class="value">${meeting.location}</span></div>
      <div class="field"><span class="label">Attendance:</span> <span class="value">${meeting.attendanceCount}/${meeting.totalMembers} (${getAttendanceRate(meeting)}%)</span></div>
      <hr style="margin:20px 0;border-color:#009A44">
      <div class="content">${meeting.minutes || "No minutes recorded."}</div>
      <div style="margin-top:40px;font-size:12px;color:#888;text-align:center">
      <p>Generated on ${format(new Date(), "MMMM d, yyyy 'at' HH:mm")}</p></div>
      </body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  // ─── RENDER ───
  if (meetingsLoading && !meetings.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009A44]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden" style={{ backgroundColor: "#fffffb" }}>
      <div className="sm:px-2 px-2 min-w-0">
        {/* Header */}
        <motion.div className="flex items-center justify-between gap-1.5 sm:gap-4 overflow-hidden mb-4" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-tight flex-shrink-0" style={{ color: "#050500" }}>
            Meetings & Attendance
          </h1>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => refetchMeetings()} className="hidden sm:flex">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={() => { resetForm(); setIsCreateOpen(true); }} className="bg-[#009A44] hover:bg-[#008238]">
              <Plus className="h-4 w-4 mr-1" /> New Meeting
            </Button>
          </div>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past">Past Meetings</TabsTrigger>
            <TabsTrigger value="attendance">Attendance</TabsTrigger>
            <TabsTrigger value="minutes">Minutes</TabsTrigger>
            <TabsTrigger value="actions">Action Items</TabsTrigger>
          </TabsList>

          {/* ═══ OVERVIEW ═══ */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Total Meetings", value: dashboardStats?.totalMeetings || 0, icon: Calendar, color: "text-blue-600", bg: "bg-blue-50" },
                { label: "Upcoming", value: dashboardStats?.upcomingMeetings || 0, icon: CalendarClock, color: "text-amber-600", bg: "bg-amber-50" },
                { label: "Completed", value: dashboardStats?.completedMeetings || 0, icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
                { label: "Avg Attendance", value: `${dashboardStats?.averageAttendance || 0}%`, icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-50" },
              ].map((stat, i) => (
                <Card key={i} className="border shadow-sm">
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${stat.bg}`}><stat.icon className={`h-5 w-5 ${stat.color}`} /></div>
                    <div>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                      <p className="text-xl font-bold">{stat.value}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Pending Minutes</CardTitle></CardHeader>
                <CardContent>
                  {(meetings.filter((m: any) => m.status === "completed" && !m.minutesPublished).slice(0, 5) || []).map((m: any) => (
                    <div key={m.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="text-sm font-medium">{m.title}</p>
                        <p className="text-xs text-gray-500">{format(new Date(m.meetingDate), "MMM d, yyyy")}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => openMinutes(m)}>Add Minutes</Button>
                    </div>
                  ))}
                  {meetings.filter((m: any) => m.status === "completed" && !m.minutesPublished).length === 0 && (
                    <p className="text-sm text-gray-500 py-4 text-center">No pending minutes</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Open Action Items</CardTitle></CardHeader>
                <CardContent>
                  {meetings.flatMap((m: any) => (m.actionItems || []).filter((i: any) => i.status !== "completed").map((i: any) => ({ ...i, meetingTitle: m.title }))).slice(0, 5).map((item: any) => (
                    <div key={item.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="text-sm font-medium">{item.description?.substring(0, 50)}</p>
                        <p className="text-xs text-gray-500">Due: {item.dueDate ? format(new Date(item.dueDate), "MMM d") : "No date"}</p>
                      </div>
                      <Badge className={PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium}>{item.priority}</Badge>
                    </div>
                  ))}
                  {meetings.flatMap((m: any) => (m.actionItems || []).filter((i: any) => i.status !== "completed")).length === 0 && (
                    <p className="text-sm text-gray-500 py-4 text-center">No open action items</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ═══ UPCOMING ═══ */}
          <TabsContent value="upcoming" className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Search meetings..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {MEETING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {meetings.filter((m: any) => m.status !== "cancelled" && m.status !== "completed").map((meeting: any) => (
              <MeetingCard key={meeting.id} meeting={meeting} onDetail={openDetail} onEdit={openEdit} onAttendance={openAttendance}
                onMinutes={openMinutes} onDelete={deleteMutation.mutate} onStatus={statusMutation.mutate}
                onDuplicate={duplicateMutation.mutate} onPrintNotice={printMeetingNotice} getTypeConfig={getTypeConfig}
                getStatusConfig={getStatusConfig} getAttendanceRate={getAttendanceRate} />
            ))}
            {meetings.filter((m: any) => m.status !== "cancelled" && m.status !== "completed").length === 0 && (
              <Card><CardContent className="py-12 text-center text-gray-500">No upcoming meetings</CardContent></Card>
            )}
          </TabsContent>

          {/* ═══ PAST ═══ */}
          <TabsContent value="past" className="space-y-4">
            {meetings.filter((m: any) => m.status === "completed" || m.status === "cancelled").map((meeting: any) => (
              <MeetingCard key={meeting.id} meeting={meeting} onDetail={openDetail} onEdit={openEdit} onAttendance={openAttendance}
                onMinutes={openMinutes} onDelete={deleteMutation.mutate} onStatus={statusMutation.mutate}
                onDuplicate={duplicateMutation.mutate} onPrintNotice={printMeetingNotice} getTypeConfig={getTypeConfig}
                getStatusConfig={getStatusConfig} getAttendanceRate={getAttendanceRate} />
            ))}
            {meetings.filter((m: any) => m.status === "completed" || m.status === "cancelled").length === 0 && (
              <Card><CardContent className="py-12 text-center text-gray-500">No past meetings</CardContent></Card>
            )}
          </TabsContent>

          {/* ═══ ATTENDANCE ═══ */}
          <TabsContent value="attendance" className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-sm">Attendance Overview</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b">
                      <th className="text-left p-2 font-medium text-gray-500">Meeting</th>
                      <th className="text-left p-2 font-medium text-gray-500">Date</th>
                      <th className="text-center p-2 font-medium text-gray-500">Present</th>
                      <th className="text-center p-2 font-medium text-gray-500">Late</th>
                      <th className="text-center p-2 font-medium text-gray-500">Absent</th>
                      <th className="text-center p-2 font-medium text-gray-500">Excused</th>
                      <th className="text-center p-2 font-medium text-gray-500">Rate</th>
                      <th className="text-center p-2 font-medium text-gray-500">Actions</th>
                    </tr></thead>
                    <tbody>
                      {meetings.map((m: any) => (
                        <tr key={m.id} className="border-b hover:bg-gray-50">
                          <td className="p-2"><span className="font-medium">{m.title}</span><br/><span className="text-xs text-gray-500">{m.meetingCode}</span></td>
                          <td className="p-2 text-gray-600">{format(new Date(m.meetingDate), "MMM d, yyyy")}</td>
                          <td className="p-2 text-center"><Badge className="bg-green-100 text-green-800">{m.attendanceCount || 0}</Badge></td>
                          <td className="p-2 text-center"><Badge className="bg-amber-100 text-amber-800">{m.lateCount || 0}</Badge></td>
                          <td className="p-2 text-center"><Badge className="bg-red-100 text-red-800">{(m.totalMembers || 0) - (m.attendanceCount || 0)}</Badge></td>
                          <td className="p-2 text-center"><Badge className="bg-gray-100 text-gray-800">{m.excusedCount || 0}</Badge></td>
                          <td className="p-2 text-center">
                            <div className="flex items-center gap-2 justify-center">
                              <Progress value={getAttendanceRate(m)} className="w-16 h-2" />
                              <span className="text-xs font-medium">{getAttendanceRate(m)}%</span>
                            </div>
                          </td>
                          <td className="p-2 text-center">
                            <Button variant="outline" size="sm" onClick={() => openAttendance(m)}>
                              <UserCheck className="h-3.5 w-3.5 mr-1" /> Manage
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ MINUTES ═══ */}
          <TabsContent value="minutes" className="space-y-4">
            {meetings.filter((m: any) => m.status === "completed").map((meeting: any) => (
              <Card key={meeting.id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{meeting.title}</p>
                    <p className="text-sm text-gray-500">{meeting.meetingCode} | {format(new Date(meeting.meetingDate), "MMM d, yyyy")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {meeting.minutesPublished ? (
                      <Badge className="bg-green-100 text-green-800">Published</Badge>
                    ) : meeting.minutes ? (
                      <Badge className="bg-amber-100 text-amber-800">Draft</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-800">No Minutes</Badge>
                    )}
                    <Button variant="outline" size="sm" onClick={() => openMinutes(meeting)}>
                      <FileText className="h-3.5 w-3.5 mr-1" /> {meeting.minutes ? "View/Edit" : "Add"}
                    </Button>
                    {meeting.minutes && <Button variant="outline" size="sm" onClick={() => printMinutes(meeting)}><Printer className="h-3.5 w-3.5 mr-1" /> Print</Button>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* ═══ ACTION ITEMS ═══ */}
          <TabsContent value="actions" className="space-y-4">
            {meetings.flatMap((m: any) =>
              (m.actionItems || []).map((item: any) => ({ ...item, meetingTitle: m.title, meetingCode: m.meetingCode, meetingId: m.id }))
            ).length > 0 ? (
              <div className="space-y-2">
                {meetings.flatMap((m: any) =>
                  (m.actionItems || []).map((item: any) => ({ ...item, meetingTitle: m.title, meetingCode: m.meetingCode, meetingId: m.id }))
                ).map((item: any) => (
                  <Card key={item.id}>
                    <CardContent className="p-4 flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          From: {item.meetingTitle} ({item.meetingCode})
                          {item.assignedTo && ` | Assigned to: ${item.assignedTo.fullName || item.assignedTo.firstName}`}
                          {item.dueDate && ` | Due: ${format(new Date(item.dueDate), "MMM d, yyyy")}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={PRIORITY_CONFIG[item.priority]}>{item.priority}</Badge>
                        <Badge variant={item.status === "completed" ? "default" : "outline"}>{item.status}</Badge>
                        {item.status !== "completed" && (
                          <Button variant="outline" size="sm" onClick={() => updateActionItemMutation.mutate({ itemId: item.id, status: "completed" })}>
                            <CheckSquare className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card><CardContent className="py-12 text-center text-gray-500">No action items</CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ═══ CREATE / EDIT DIALOG ═══ */}
      <Dialog open={isCreateOpen || isEditOpen} onOpenChange={(open) => { if (!open) { setIsCreateOpen(false); setIsEditOpen(false); setEditingMeeting(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditOpen ? "Edit Meeting" : "Create New Meeting"}</DialogTitle>
            <DialogDescription>{isEditOpen ? "Update meeting details" : "Schedule a new meeting"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Meeting title" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={form.meetingType} onValueChange={(v) => setForm({ ...form, meetingType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{MEETING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date *</Label><Input type="date" value={form.meetingDate} onChange={(e) => setForm({ ...form, meetingDate: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Time *</Label><Input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} /></div>
              <div><Label>End Time *</Label><Input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} /></div>
            </div>
            <div><Label>Location *</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Meeting location" /></div>
            <div><Label>Online Link</Label><Input value={form.onlineLink} onChange={(e) => setForm({ ...form, onlineLink: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Agenda</Label><Textarea value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} rows={4} placeholder="Meeting agenda items..." /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            <div className="flex items-center gap-2">
              <Checkbox checked={form.requiredAttendance} onCheckedChange={(c) => setForm({ ...form, requiredAttendance: c as boolean })} />
              <Label>Required attendance for all members</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateOpen(false); setIsEditOpen(false); setEditingMeeting(null); }}>Cancel</Button>
            <Button className="bg-[#009A44] hover:bg-[#008238]"
              disabled={!form.title || !form.meetingDate || !form.startTime || !form.location}
              onClick={() => {
                if (isEditOpen && editingMeeting) {
                  updateMutation.mutate({ id: editingMeeting.id, ...form });
                } else {
                  createMutation.mutate(form);
                }
              }}>
              {isEditOpen ? "Update" : "Create"} Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ DETAIL SHEET ═══ */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedMeeting?.title}</SheetTitle>
            <SheetDescription>{selectedMeeting?.meetingCode}</SheetDescription>
          </SheetHeader>
          {selectedMeeting && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gray-400" /><span>{format(new Date(selectedMeeting.meetingDate), "MMM d, yyyy")}</span></div>
                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gray-400" /><span>{selectedMeeting.startTime} - {selectedMeeting.endTime}</span></div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gray-400" /><span>{selectedMeeting.location}</span></div>
                <div className="flex items-center gap-2"><Badge className={getTypeConfig(selectedMeeting.meetingType).color}>{getTypeConfig(selectedMeeting.meetingType).label}</Badge></div>
              </div>
              <Badge className={getStatusConfig(selectedMeeting.status).color}>{selectedMeeting.status}</Badge>
              {selectedMeeting.onlineLink && (
                <div className="flex items-center gap-2 text-sm"><LinkIcon className="h-4 w-4 text-blue-500" /><a href={selectedMeeting.onlineLink} target="_blank" className="text-blue-600 underline">{selectedMeeting.onlineLink}</a></div>
              )}
              {selectedMeeting.agenda && (
                <div><p className="text-sm font-medium mb-1">Agenda</p><p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedMeeting.agenda}</p></div>
              )}
              <Separator />
              <div><p className="text-sm font-medium mb-2">Attendance</p>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-green-50 p-2 rounded"><p className="text-lg font-bold text-green-600">{selectedMeeting.attendanceCount || 0}</p><p className="text-xs text-gray-500">Present</p></div>
                  <div className="bg-amber-50 p-2 rounded"><p className="text-lg font-bold text-amber-600">{selectedMeeting.lateCount || 0}</p><p className="text-xs text-gray-500">Late</p></div>
                  <div className="bg-red-50 p-2 rounded"><p className="text-lg font-bold text-red-600">{(selectedMeeting.totalMembers || 0) - (selectedMeeting.attendanceCount || 0)}</p><p className="text-xs text-gray-500">Absent</p></div>
                  <div className="bg-gray-50 p-2 rounded"><p className="text-lg font-bold text-gray-600">{getAttendanceRate(selectedMeeting)}%</p><p className="text-xs text-gray-500">Rate</p></div>
                </div>
              </div>
              {selectedMeeting.minutes && (
                <div><p className="text-sm font-medium mb-1">Minutes</p><p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedMeeting.minutes}</p></div>
              )}
              <div className="flex gap-2 flex-wrap pt-2">
                {selectedMeeting.status === "scheduled" && <Button size="sm" className="bg-[#009A44]" onClick={() => statusMutation.mutate({ id: selectedMeeting.id, action: "start" })}>Start Meeting</Button>}
                {selectedMeeting.status === "ongoing" && <Button size="sm" className="bg-[#009A44]" onClick={() => { setSelectedMeeting(selectedMeeting); setIsAttendanceOpen(true); }}>Manage Attendance</Button>}
                <Button variant="outline" size="sm" onClick={() => printMeetingNotice(selectedMeeting)}>Print Notice</Button>
                <Button variant="outline" size="sm" onClick={() => printAttendanceSheet(selectedMeeting)}>Print Attendance</Button>
                {selectedMeeting.minutes && <Button variant="outline" size="sm" onClick={() => printMinutes(selectedMeeting)}>Print Minutes</Button>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ═══ ATTENDANCE SHEET ═══ */}
      <Sheet open={isAttendanceOpen} onOpenChange={setIsAttendanceOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Attendance - {selectedMeeting?.title}</SheetTitle>
            <SheetDescription>{selectedMeeting?.meetingCode} | {selectedMeeting && format(new Date(selectedMeeting.meetingDate), "MMM d, yyyy")}</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            {attendanceRecords.map((rec, idx) => (
              <div key={rec.teacherId} className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium">{rec.teacherName}</p>
                </div>
                <Select value={rec.status} onValueChange={(v) => {
                  const updated = [...attendanceRecords];
                  updated[idx] = { ...updated[idx], status: v };
                  setAttendanceRecords(updated);
                }}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="excused">Excused</SelectItem>
                  </SelectContent>
                </Select>
                <Input className="w-32" placeholder="Notes" value={rec.notes} onChange={(e) => {
                  const updated = [...attendanceRecords];
                  updated[idx] = { ...updated[idx], notes: e.target.value };
                  setAttendanceRecords(updated);
                }} />
              </div>
            ))}
            {attendanceRecords.length === 0 && <p className="text-center text-gray-500 py-8">No attendance records found</p>}
            <Button className="w-full bg-[#009A44] hover:bg-[#008238]"
              onClick={() => bulkAttendanceMutation.mutate({ meetingId: selectedMeeting?.id, records: attendanceRecords.map((r) => ({ teacherId: r.teacherId, status: r.status, notes: r.notes })) })}>
              Save Attendance
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ═══ MINUTES DIALOG ═══ */}
      <Dialog open={isMinutesOpen} onOpenChange={setIsMinutesOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Meeting Minutes</DialogTitle>
            <DialogDescription>{selectedMeeting?.title} ({selectedMeeting?.meetingCode})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea value={minutesContent} onChange={(e) => setMinutesContent(e.target.value)} rows={12} placeholder="Enter meeting minutes..." />
            <div className="flex gap-2">
              <Button className="bg-[#009A44] hover:bg-[#008238]" onClick={() => minutesMutation.mutate({ meetingId: selectedMeeting?.id, content: minutesContent, title: `Minutes for ${selectedMeeting?.title}` })}>
                Save Minutes
              </Button>
              {selectedMeeting?.meetingMinutes?.[0] && !selectedMeeting.minutesPublished && (
                <Button variant="outline" onClick={() => publishMinutesMutation.mutate({ meetingId: selectedMeeting.id, minutesId: selectedMeeting.meetingMinutes[0].id })}>
                  Publish Minutes
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ DECISION DIALOG ═══ */}
      <Dialog open={isDecisionOpen} onOpenChange={setIsDecisionOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record Decision</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Description *</Label><Textarea value={decisionForm.description} onChange={(e) => setDecisionForm({ ...decisionForm, description: e.target.value })} rows={3} /></div>
            <div><Label>Category</Label><Input value={decisionForm.category} onChange={(e) => setDecisionForm({ ...decisionForm, category: e.target.value })} placeholder="e.g., Financial, Operational" /></div>
            <div><Label>Notes</Label><Textarea value={decisionForm.notes} onChange={(e) => setDecisionForm({ ...decisionForm, notes: e.target.value })} rows={2} /></div>
            <Button className="bg-[#009A44]" disabled={!decisionForm.description} onClick={() => decisionMutation.mutate({ meetingId: selectedMeeting?.id, ...decisionForm })}>
              Record Decision
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ ACTION ITEM DIALOG ═══ */}
      <Dialog open={isActionItemOpen} onOpenChange={setIsActionItemOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create Action Item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Description *</Label><Textarea value={actionItemForm.description} onChange={(e) => setActionItemForm({ ...actionItemForm, description: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To</Label>
                <Select value={actionItemForm.assignedToId} onValueChange={(v) => setActionItemForm({ ...actionItemForm, assignedToId: v })}>
                  <SelectTrigger><SelectValue placeholder="Select member" /></SelectTrigger>
                  <SelectContent>
                    {teachers.map((t: any) => <SelectItem key={t.id} value={String(t.id)}>{t.fullName || `${t.firstName} ${t.lastName}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Due Date</Label><Input type="date" value={actionItemForm.dueDate} onChange={(e) => setActionItemForm({ ...actionItemForm, dueDate: e.target.value })} /></div>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={actionItemForm.priority} onValueChange={(v) => setActionItemForm({ ...actionItemForm, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="bg-[#009A44]" disabled={!actionItemForm.description} onClick={() => actionItemMutation.mutate({ meetingId: selectedMeeting?.id, ...actionItemForm })}>
              Create Action Item
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Meeting Card Component ───
function MeetingCard({ meeting, onDetail, onEdit, onAttendance, onMinutes, onDelete, onStatus, onDuplicate, onPrintNotice, getTypeConfig, getStatusConfig, getAttendanceRate }: any) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-mono text-gray-500">{meeting.meetingCode}</span>
            <Badge className={getTypeConfig(meeting.meetingType).color}>{getTypeConfig(meeting.meetingType).label}</Badge>
            <Badge className={getStatusConfig(meeting.status).color}>{meeting.status}</Badge>
          </div>
          <h3 className="font-semibold text-sm sm:text-base truncate">{meeting.title}</h3>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{format(new Date(meeting.meetingDate), "MMM d, yyyy")}</span>
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{meeting.startTime} - {meeting.endTime}</span>
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{meeting.location}</span>
            <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{meeting.attendanceCount || 0}/{meeting.totalMembers || 0} ({getAttendanceRate(meeting)}%)</span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onDetail(meeting)}><Eye className="h-4 w-4 mr-2" />View Details</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(meeting)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAttendance(meeting)}><UserCheck className="h-4 w-4 mr-2" />Attendance</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onMinutes(meeting)}><FileText className="h-4 w-4 mr-2" />Minutes</DropdownMenuItem>
            <DropdownMenuSeparator />
            {meeting.status === "scheduled" && <DropdownMenuItem onClick={() => onStatus({ id: meeting.id, action: "start" })}><Play className="h-4 w-4 mr-2" />Start</DropdownMenuItem>}
            {meeting.status === "ongoing" && <DropdownMenuItem onClick={() => onStatus({ id: meeting.id, action: "complete" })}><CheckCircle className="h-4 w-4 mr-2" />Complete</DropdownMenuItem>}
            {meeting.status !== "cancelled" && meeting.status !== "completed" && <DropdownMenuItem onClick={() => onStatus({ id: meeting.id, action: "cancel" })}><Ban className="h-4 w-4 mr-2" />Cancel</DropdownMenuItem>}
            <DropdownMenuItem onClick={() => onDuplicate(meeting.id)}><Copy className="h-4 w-4 mr-2" />Duplicate</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPrintNotice(meeting)}><Printer className="h-4 w-4 mr-2" />Print Notice</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-600" onClick={() => { if (confirm("Delete this meeting?")) onDelete(meeting.id); }}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </motion.div>
  );
}
